import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' 
    ? 'https://nownextnotyet.syllamatic.com/api'
    : 'http://localhost:5000/api'
);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let _unauthorizedHandler = null;
export const setUnauthorizedHandler = (fn) => { _unauthorizedHandler = fn; };

// Attach Bearer token on every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('nnn_auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 globally (expired/invalid session)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && _unauthorizedHandler) {
      _unauthorizedHandler();
    }
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Projects API
export const projectsApi = {
  getAll: () => api.get('/projects'),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  bulkDelete: (ids) => api.post('/projects/bulk-delete', { ids }),
};

// Employees API
export const employeesApi = {
  getAll: () => api.get('/employees'),
  get: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
};

// Parameters API
export const parametersApi = {
  getAll: () => api.get('/parameters'),
  get: (id) => api.get(`/parameters/${id}`),
  create: (data) => api.post('/parameters', data),
  update: (id, data) => api.put(`/parameters/${id}`, data),
  delete: (id) => api.delete(`/parameters/${id}`),
};

// Assignments API
export const assignmentsApi = {
  getAll: (params) => api.get('/assignments', { params }),
  get: (id) => api.get(`/assignments/${id}`),
  create: (data) => api.post('/assignments', data),
  update: (id, data) => api.put(`/assignments/${id}`, data),
  delete: (id) => api.delete(`/assignments/${id}`),
};

// Prioritization API
export const prioritizationApi = {
  generate: (data) => api.post('/prioritize', data),
  getParameters: () => api.get('/prioritize'),
};

// Integrations API
export const integrationsApi = {
  getSettings: () => api.get('/integrations/settings'),
  saveSettings: (data) => api.post('/integrations/settings', data),
  testGitLab: () => api.get('/integrations/gitlab/test'),
  syncGitLab: () => api.post('/integrations/gitlab/sync'),
  getSyncLog: (limit = 50) => api.get('/integrations/gitlab/log', { params: { limit } }),
  getGitLabProjects: () => api.get('/integrations/gitlab/projects'),
  importGitLabClients: (clients) => api.post('/integrations/gitlab/import-clients', { clients }),
};

// Schedule API
export const scheduleApi = {
  get: () => api.get('/schedule'),
  compute: (data) => api.post('/schedule', data || {}),
};

// Clients API
export const clientsApi = {
  getAll: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getLogoUrl: (id) => `${API_BASE_URL}/clients/${id}/logo`,
};

// Extract API
export const extractApi = {
  preview: (url) => api.post('/extract/gitlab-comment', { url }),
  downloadDocx: (url) => api.post('/extract/gitlab-comment/docx', { url }, { responseType: 'blob' }),
};

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  refresh: () => api.post('/auth/refresh'),
  me: () => api.get('/auth/me'),
  getSessionSettings: () => api.get('/auth/session-settings'),
  updateSessionSettings: (data) => api.put('/auth/session-settings', data),
};

export default api;